/**
 * Tutorial gate (DESIGN-client §6): when `src/tutorial/Tutorial.tsx` exists (C-T23) it is
 * lazy-loaded here; until then a minimal "First hand" placeholder with the persistent
 * "Skip, deal me in" that marks `tutorialDone` and continues to the lobby.
 */
import { type ComponentType, Suspense, lazy } from 'react';
import { store, useStore } from '../../store/store';
import { Button, Panel } from '../ui';

const tutorialModules = import.meta.glob<{ Tutorial?: ComponentType; default?: ComponentType }>('../../tutorial/Tutorial.tsx');
const loader = Object.values(tutorialModules)[0];
const LazyTutorial = loader
  ? lazy(async () => {
      const m = await loader();
      const C = m.Tutorial ?? m.default;
      if (!C) throw new Error('tutorial module has no component');
      return { default: C };
    })
  : null;

export function skipTutorial(): void {
  const st = store.getState();
  st.setSettings({ tutorialDone: true });
  st.setPresentation({ screen: 'lobby' });
}

export function TutorialGate() {
  if (LazyTutorial) {
    return (
      <Suspense fallback={<div className="screen screen--center" data-testid="screen-tutorial" />}>
        <LazyTutorial />
      </Suspense>
    );
  }
  return <TutorialPlaceholder />;
}

/**
 * The gate's fallback branch, on its own so the design lab can render it while `Tutorial.tsx`
 * exists — otherwise the placeholder copy is unreachable the moment the real tutorial ships, and
 * it is still what a build without the tutorial chunk shows.
 */
export function TutorialPlaceholder() {
  const me = useStore((s) => s.session.me);
  return (
    <div className="screen screen--center" data-testid="screen-tutorial">
      <div className="screen__inner" style={{ textAlign: 'center' }}>
        <div className="eyebrow">First hand</div>
        <h1 className="display display--lg">{me ? `${me.name}, ` : ''}let&apos;s deal one</h1>
        <Panel felt>
          <p className="muted" style={{ margin: 0 }}>
            The guided first hand lands with the table scene. For now, jump straight to a game: bots fill the other seats.
          </p>
        </Panel>
        <Button variant="primary" size="lg" block testId="skip-tutorial" onClick={skipTutorial}>
          Skip, deal me in
        </Button>
      </div>
    </div>
  );
}
